import { Component, EventEmitter, inject, signal } from '@angular/core';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { InstallableService } from './installable.service';
import { openSuccessToast, openWarningToast } from '@pct-ng/feature-message-material';
import { showUnknownErrorDialog } from '@pct-ng/core/error-http';
import { UAParser } from 'ua-parser-js';
import { MatBottomSheet, MatBottomSheetModule } from '@angular/material/bottom-sheet';
import { InstallInformationPanelComponent } from './install-information-panel.component';
import { HttpClient } from '@angular/common/http';
import { catchError, type Observable, of, switchMap, tap, throwError } from 'rxjs';

@Component({
  selector: 'app-pwa-install-overlay-panel',
  imports: [MatButton, MatIconButton, MatIcon, MatProgressSpinner, MatBottomSheetModule],
  host: {
    class:
      'block relative max-w-sm w-[360px] bg-[#2c2c2c] border-[#2c2c2c] rounded-xl shadow-lg border p-4 flex flex-col gap-3 top-2',
  },
  templateUrl: './pwa-install-overlay-panel.component.html',
})
export class PwaInstallOverlayPanelComponent {
  readonly #http = inject(HttpClient);
  readonly #matBottomSheet = inject(MatBottomSheet);
  readonly #installableService = inject(InstallableService);
  protected readonly loading = signal(false);

  readonly close = new EventEmitter<void>();

  protected async onClickInstall(): Promise<void> {
    this.loading.set(true);

    const isApple = ['macos', 'ios'].includes(
      (UAParser(window.navigator.userAgent).os.name ?? '').toLowerCase(),
    );

    if (this.#installableService.installable() === 'SUPPORTED' && !isApple) {
      try {
        await this.#installableService.installApp();
        if (this.#installableService.installable() === 'DENIED') {
          openWarningToast({
            message: 'A telepítés elutasításra került.',
          });
        } else {
          openSuccessToast({
            message: 'Telepítés folyamatban…',
          });
        }
        // eslint-disable-next-line sonarjs/no-ignored-exceptions
      } catch (_e: any) {
        showUnknownErrorDialog(undefined, undefined, undefined, 'PIOPC1');
      } finally {
        this.loading.set(false);
        this.onClickClose();
      }
    } else {
      const { browserName, osName, osVersion } = this.#detectBrowserAndOSTypeAndVersion();
      console.log('xxxxxx', browserName, osName, osVersion);

      const maybeUserBrowserUILanguage = Intl.DateTimeFormat()
        .resolvedOptions()
        .locale.split('-')[0];
      let text$: Observable<string> | null = null;
      switch (osName) {
        case 'ios':
          text$ = this.#showIOSBottomSheet(browserName, maybeUserBrowserUILanguage);
          break;
        case 'android':
          text$ = this.#showAndroidBottomSheet(osVersion, maybeUserBrowserUILanguage);
          break;
        case 'macos':
          text$ = this.#showMacosBottomSheet(browserName, maybeUserBrowserUILanguage);
          break;
        case 'windows':
          text$ = this.#showWindowsBottomSheet(browserName, maybeUserBrowserUILanguage);
          break;
      }
      // TODO csak tesztig
      // const text$ = this.#showIOSBottomSheet(browserName, maybeUserBrowserUILanguage);

      if (text$ === null) {
        text$ = throwError(() => new Error(''));
      }

      text$
        .pipe(
          tap(() => this.onClickClose()),
          catchError(() => {
            this.onClickClose();
            return of('**Ismeretlen böngésző, kérjük forduljon a support-hoz.**');
          }),
          switchMap(text =>
            this.#matBottomSheet
              .open(InstallInformationPanelComponent, {
                data: {
                  text,
                },
              })
              .afterOpened(),
          ),
        )
        .subscribe();
    }
  }

  protected onClickClose(): void {
    this.close.emit();
  }

  #detectBrowserAndOSTypeAndVersion(): {
    browserName: string;
    osName: string;
    osVersion: string;
  } {
    const parser = UAParser(window.navigator.userAgent);
    const browser = parser.browser.name;
    const os = parser.os.name;

    const browserName = browser?.toLowerCase() ?? 'Ismeretlen böngésző';
    const osName = os?.toLowerCase() ?? 'Ismeretlen OS';
    const osVersion = parser.os?.version?.toLowerCase() ?? 'Ismeretlen verzió';

    return { browserName, osName, osVersion };
  }

  #showIOSBottomSheet(
    browserName: string,
    maybeUserBrowserUILanguage: string,
  ): Observable<string> | null {
    if (
      !(
        ['safari', 'chrome', 'mobile safari', 'mobile chrome'].includes(browserName) &&
        ['hu', 'en'].includes(maybeUserBrowserUILanguage)
      )
    ) {
      return null;
    }
    return this.#http.get(
      `pwa/install/description/ios_${browserName.replace(' ', '_')}_${maybeUserBrowserUILanguage}.md`,
      {
        responseType: 'text',
      },
    );
  }

  #showAndroidBottomSheet(
    osVersion: string,
    maybeUserBrowserUILanguage: string,
  ): Observable<string> | null {
    if (
      !(['14', '15', '16'].includes(osVersion) && ['hu', 'en'].includes(maybeUserBrowserUILanguage))
    ) {
      return null;
    }
    return this.#http.get(
      `pwa/install/description/android_${osVersion === '14' ? osVersion : '15_16'}_${maybeUserBrowserUILanguage}.md`,
      {
        responseType: 'text',
      },
    );
  }

  #showMacosBottomSheet(
    browserName: string,
    maybeUserBrowserUILanguage: string,
  ): Observable<string> | null {
    if (
      !(
        ['safari', 'chrome'].includes(browserName) &&
        ['hu', 'en'].includes(maybeUserBrowserUILanguage)
      )
    ) {
      return null;
    }
    return this.#http.get(
      `pwa/install/description/macos_${browserName}_${maybeUserBrowserUILanguage}.md`,
      {
        responseType: 'text',
      },
    );
  }

  #showWindowsBottomSheet(
    browserName: string,
    maybeUserBrowserUILanguage: string,
  ): Observable<string> | null {
    if (
      !(
        ['edge', 'chrome'].includes(browserName) &&
        ['hu', 'en'].includes(maybeUserBrowserUILanguage)
      )
    ) {
      return null;
    }
    return this.#http.get(
      `pwa/install/description/windows_${browserName}_${maybeUserBrowserUILanguage}.md`,
      {
        responseType: 'text',
      },
    );
  }
}
